import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AntibioticCategory,
  City,
  Country,
  Debridement,
  GetListSurgeryModel,
  Medication,
  MedicationCategory,
  Nationality,
  Occupation,
  State,
  VacuumAssistedClosureModel,
} from '../../models/patient/patient.model';
import { ApiService } from '../api/api.service';
import { PatientDetailResponse } from '../../models/patient/patient-detail.model';
import { CallContact } from '../../models/patient/call-contacts.model';
import { CurrentUserService } from '../auth/current-user.service';
import { UserService } from '../user/user.service';
import { UserRole } from '../../../../enums/roles.enum';
import { PermissionService } from '../user/permission.service';

@Injectable({
  providedIn: 'root',
})
export class PatientsService {
  constructor(
    private apiService: ApiService,
    private currentUserService: CurrentUserService,
    private userService: UserService,
    private permissionService: PermissionService,
  ) {}

  private apiURL = `${environment.apiLocations.patient}/Patient`;
  //#region Patients
  private getListPatientUrl = `${this.apiURL}/getListPatient`;
  private createPatientUrl = `${this.apiURL}/createPatient`;
  private updatePatientUrl = `${this.apiURL}/updatePatient`;
  private deletePatientUrl = `${this.apiURL}/deletePatient`;
  private getPatientByIdUrl = `${this.apiURL}/getPatientById`;
  private generatePatientQrCodeForConsultationUrl = `${this.apiURL}/generatePatientQrCodeForConsultation`;
  private checkPatientExistenceUrl = `${this.apiURL}/checkPatientExistence`;
  private updatePatientABIUrl = `${this.apiURL}/update-abi`;
  //#endregion

  getListPatient(
    page: number,
    size: number,
    filters: any = {},
  ): Observable<any> {
    let url = `${this.getListPatientUrl}?PageIndex=${page}&PageSize=${size}`;
    if (filters.name) {
      url += `&Name=${filters.name}`;
    }

    if (filters.pid) {
      url += `&PID=${filters.pid}`;
    }

    if (filters.startCreatedDate) {
      const startDate = new Date(filters.startCreatedDate);
      startDate.setUTCHours(0, 0, 0, 0);
      url += `&StartCreatedDateTime=${startDate.toISOString()}`;
    }

    if (filters.endCreatedDate) {
      const endDate = new Date(filters.endCreatedDate);
      endDate.setUTCHours(23, 59, 59, 999);
      url += `&EndCreatedDateTime=${endDate.toISOString()}`;
    }

    // if (filters.status) {
    //   url += `&Status=${filters.status}`;
    // }

    if (filters.organizationId) {
      url += `&OrganizationId=${filters.organizationId}`;
    }

    if (filters.sortBy) {
      url += `&SortBy=${filters.sortBy}`;
    }
    if (filters.sortDirection) {
      url += `&SortDirection=${filters.sortDirection}`;
    }

    return this.apiService.get<any>(url);
  }

  createPatient(patientData: any): Observable<any> {
    let url = `${this.createPatientUrl}`;

    return this.apiService.post<any>(url, patientData);
  }

  checkPatientExistence(pid: string, organizationId: string): Observable<any> {
    const url = `${this.checkPatientExistenceUrl}?Pid=${pid}&OrganizationId=${organizationId}`;
    return this.apiService.get<any>(url);
  }

  dashboardStatistics(periodType: number): Observable<any> {
    return this.apiService
      .get<any>(
        `${environment.apiLocations.patient}/patient/getListDashboardItem/?periodType=${periodType}`,
      )
      .pipe(map((res) => res?.data ?? []));
  }

  getPatientById(patientId: string): Observable<PatientDetailResponse> {
    const url = `${this.getPatientByIdUrl}?Id=${patientId}`;
    return this.apiService.get<any>(url).pipe(map((res) => res?.data ?? null));
  }

  deletePatient(id: string): Observable<any> {
    return this.apiService.post<any>(this.deletePatientUrl, { id });
  }

  updatePatient(patientData: any): Observable<any> {
    return this.apiService.post<any>(this.updatePatientUrl, patientData);
  }

  updatePatientABI(patientId: string, abiValue: number | null): Observable<any> {
    return this.apiService.post<any>(this.updatePatientABIUrl, {
      patientId,
      abiValue,
    });
  }

  //#region Dropdowns
  getListNationality(): Observable<Nationality[]> {
    return this.apiService
      .get<any>(
        `${environment.apiLocations.patient}/nationality/getListNationality`,
      )
      .pipe(map((res) => res?.data ?? []));
  }

  getListOccupation(): Observable<Occupation[]> {
    return this.apiService
      .get<any>(
        `${environment.apiLocations.patient}/occupation/getListOccupation`,
      )
      .pipe(map((res) => res?.data ?? []));
  }

  getListCountry(): Observable<Country[]> {
    return this.apiService
      .get<any>(`${environment.apiLocations.patient}/country/getListCountry`)
      .pipe(map((res) => res?.data ?? []));
  }

  getListState(countryId: number): Observable<State[]> {
    return this.apiService
      .get<any>(
        `${environment.apiLocations.patient}/country/getListState?CountryId=${countryId}`,
      )
      .pipe(map((res) => res?.data ?? []));
  }

  getListCity(stateId: number): Observable<City[]> {
    return this.apiService
      .get<any>(
        `${environment.apiLocations.patient}/country/getListCity?StateId=${stateId}`,
      )
      .pipe(map((res) => res?.data ?? []));
  }

  getListMedication(): Observable<MedicationCategory[]> {
    return this.apiService
      .get<any>(
        `${environment.apiLocations.patient}/medication/getListMedication`,
      )
      .pipe(map((res) => res?.data ?? []));
  }

  getListSurgery(): Observable<GetListSurgeryModel[]> {
    return this.apiService
      .get<any>(`${environment.apiLocations.patient}/surgery/getListSurgery`)
      .pipe(map((res) => res?.data ?? []));
  }

  getListAntibiotic(): Observable<AntibioticCategory[]> {
    return this.apiService
      .get<any>(
        `${environment.apiLocations.patient}/antibiotic/getListAntibiotic`,
      )
      .pipe(map((res) => res?.data ?? []));
  }

  getListDebridement(): Observable<Debridement[]> {
    return this.apiService
      .get<any>(
        `${environment.apiLocations.patient}/debridement/getListDebridement`,
      )
      .pipe(map((res) => res?.data ?? []));
  }

  getListVacuumAssistedClosure(): Observable<VacuumAssistedClosureModel[]> {
    return this.apiService
      .get<any>(
        `${environment.apiLocations.patient}/vacuumAssistedClosure/getListVacuumAssistedClosureQuery`,
      )
      .pipe(map((res) => res?.data ?? []));
  }

  getById(patientId: string): Observable<any> {
    let url = `${this.getPatientByIdUrl}?Id=${patientId}`;

    return this.apiService.get<any>(url).pipe(map((res) => res?.data));
  }

  generatePatientQrCodeForConsultation(
    patientId: string,
    nurseId: string,
    nurseName: string,
  ): Observable<any> {
    let url = `${this.generatePatientQrCodeForConsultationUrl}`;

    return this.apiService.postBlob(url, {
      patientId: patientId,
      nurseId: nurseId,
      nurseName: nurseName,
    });
  }

  getCallContacts(patientId: string): Observable<CallContact[]> {
    const isNurse = this.permissionService.hasPermission('nurse_claim');
    const isSpecialist =
      this.permissionService.hasPermission('specialist_claim');

    let roleType: number;
    if (isNurse) {
      roleType = UserRole.Specialist;
    } else if (isSpecialist) {
      roleType = UserRole.Nurse;
    } else {
      roleType = UserRole.Specialist;
    }

    return this.userService.getListUserByRoleType(roleType).pipe(
      map((res) => {
        const users = res?.data ?? [];
        return users.map((user: any) => ({
          id: user.id,
          fullName: user.name || user.userName || 'Unknown',
        }));
      }),
    );
  }

  //#endregion
}
